import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { EditNote, Warehouse } from "@mui/icons-material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useTranslation } from "../../../../../hooks/useTranslation";

type MessageTarget = "ORDER" | "WAREHOUSE";

const MAX_LENGTH = 2000;

/** 🔥 preset sistemi */
const WAREHOUSE_PRESETS = [
  {
    key: "dontSend",
    labelKey: "dontSendToCustomer",
    value: "Don't send to customer",
    color: "error" as const,
  },
  {
    key: "canSend",
    label: "Now can send to customer",
    value: "Now can send to customer",
    color: "success" as const,
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export const AddMessageDialog = ({ open, onClose }: Props) => {
  const { orderId } = useParams<{ orderId: string }>();
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const invalidate = useInvalidate();

  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.notes",
  );

  const [target, setTarget] = useState<MessageTarget>("ORDER");
  const [message, setMessage] = useState("");

  const { mutate, isLoading } = useCustomMutation();

  const handleSend = () => {
    if (!message.trim() || !selectedCountry) return;

    const url =
      target === "ORDER"
        ? `store/${selectedCountry.id}/order/${orderId}/notes`
        : `store/${selectedCountry.id}/order/${orderId}/warehouse-messages`;

    mutate(
      {
        url,
        method: "post",
        values: { message: message.trim() },
        successNotification: () => ({
          message: translate("sendSuccess"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          setMessage("");
          setTarget("ORDER");
          onClose();
          invalidate({ resource: "all", invalidates: ["all"] });
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>{translate("addMessage")}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {/* TARGET */}
          <Tabs
            value={target}
            onChange={(_, v) => {
              setTarget(v);
              setMessage("");
            }}
            variant="fullWidth"
          >
            <Tab
              value="ORDER"
              icon={<EditNote />}
              iconPosition="start"
              label={translate("addNote")}
              sx={{ textTransform: "none" }}
            />

            <Tab
              value="WAREHOUSE"
              icon={<Warehouse />}
              iconPosition="start"
              label={translate("warehouseChat")}
              sx={{ textTransform: "none" }}
            />
          </Tabs>

          {/* PRESETS */}
          {target === "WAREHOUSE" && (
            <Stack
              spacing={1}
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => t.palette.action.hover,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {translate("quickMessage")}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {WAREHOUSE_PRESETS.map((preset) => {
                  const label = preset.labelKey
                    ? translate(preset.labelKey)
                    : preset.label;

                  return (
                    <Chip
                      key={preset.key}
                      size="small"
                      label={label}
                      clickable
                      color={preset.color}
                      variant={message === preset.value ? "filled" : "outlined"}
                      onClick={() => setMessage(preset.value)}
                    />
                  );
                })}
              </Stack>
            </Stack>
          )}

          {/* INPUT */}
          <TextField
            multiline
            minRows={3}
            maxRows={5}
            size="small"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              target === "ORDER"
                ? translate("typeNote")
                : translate("typeWarehouseMessage")
            }
            error={message.length > MAX_LENGTH}
            helperText={`${message.length}/${MAX_LENGTH}`}
            FormHelperTextProps={{ sx: { textAlign: "right" } }}
          />

          {/* ACTION */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              endIcon={<SendIcon fontSize="small" />}
              disabled={
                !message.trim() || isLoading || message.length > MAX_LENGTH
              }
              onClick={handleSend}
            >
              {translateWithoutPrefix("buttons.send")}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
